import { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../../i18n";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
  banner?: React.ReactNode;
  leading?: React.ReactNode;
  footer?: React.ReactNode;
  fillHeight?: boolean;
  children: React.ReactNode;
}

const SIZE_MAP: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

export default function Modal({
  isOpen,
  onClose,
  title,
  size = "md",
  banner,
  leading,
  footer,
  fillHeight = false,
  children,
}: ModalProps) {
  const { dir, t } = useTranslation();
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      dir={dir}
      className="fixed inset-0 z-200 flex items-end justify-center p-0 pt-6 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-overlay backdrop-blur-sm" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`relative flex min-h-0 max-h-[calc(100dvh-env(safe-area-inset-bottom))] w-full ${SIZE_MAP[size]} flex-col overflow-hidden rounded-t-2xl bg-card text-card-foreground shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl ${fillHeight ? "h-[min(90dvh,52rem)] sm:h-[min(85dvh,52rem)]" : ""}`}
        style={{ animation: "modalIn 0.2s ease-out" }}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || leading) && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-4 sm:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {leading ? <div className="shrink-0">{leading}</div> : null}
              {title ? (
                <h2 id={titleId} className="min-w-0 text-base font-bold text-card-foreground sm:text-lg">
                  {title}
                </h2>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent text-muted-foreground hover:text-accent-foreground transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="w-5 h-5"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {banner ? (
          <div className="shrink-0 border-b border-border px-5 py-3 sm:px-6">
            {banner}
          </div>
        ) : null}
        <div
          className={
            footer
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:px-6"
          }
        >
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-border bg-card pb-[env(safe-area-inset-bottom)]">
            {footer}
          </div>
        ) : null}
      </div>
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
